import { Address, Location, LocationContext, Organization, PolicyType } from "."
import { DeliveryClass, EndUserType, ShippingType } from "../../common"
import dayjs from "dayjs"
import { LoadWithOrderState } from "../../index"

type ReportEventTypes =
  | "pickUp"
  | "holdPickUp"
  | "targetStationArrive"
  | "targetStationUnload"
  | "sourceStationLoad"
  | "classify"
  | "sourceStationDepart"
  | "holdDelivery"
  | "aheadDeliver"
  | "deliver"
  | "quit"

type PlannedInfo = {
  inCollect: {
    driverId: string
    driverOrganizationId: string
    driverName: string
    driverPhone: string
  } | null
  inDelivery: {
    driverId: string
    driverOrganizationId: string
    driverName: string
    driverPhone: string
  } | null
  inTransport: {
    driverId: string
    driverOrganizationId: string
    driverName: string
    driverPhone: string
  } | null
}

type ReportTimeKeys = {
  [key in ReportEventTypes]: `${key}Time`
}[ReportEventTypes]

export interface PIL_Load extends Omit<Load, "sender" | "receiver"> {
  sender: EndUserInfo & {
    pilName?: string
  }
  receiver: EndUserInfo & {
    pilName?: string
  }
}

export interface Load {
  id: string
  invoiceNumber: string
  /**
   * 대표 표기 송장번호. 위탁택배 송장이나 고객사 송장이 있으면 그 값이고, 없으면
   * `invoiceNumber` 다. 서버가 레거시 화물까지 보정해 항상 채워 보낸다.
   *
   * **이슈·CS 티켓을 운송장으로 찾을 때는 이 값을 쓴다** — 그쪽 filter 키가
   * `display_invoice_number` 완전 일치라서 `invoiceNumber` 로는 걸리지 않는다.
   */
  displayInvoiceNumber: string
  clientId?: string
  clientName: string
  clientOrderId?: string
  clientShippingId?: string
  name: string
  state:
    | "PENDING_COLLECT"
    | "HOLDING_COLLECT"
    | "IN_COLLECT"
    | "IN_TRANSPORT"
    | "IN_DELIVERY"
    | "HOLDING_DELIVERY"
    | "AT_STATION"
    | "DELIVERED"
    | "QUIT"
    | "LOST"
  substate: "BEFORE_SORTING" | "STAND_BY" | "RESUMABLE" | null
  deliveryClass: DeliveryClass
  shippingType: ShippingType
  endUserType: EndUserType
  orderTime: string
  takeOutTime: string | null
  readyTime: string | null
  standByTime: string | null
  targetDeliverTime: string | null
  cartId: string | null
  truckId: string | null
  plannedInfo: PlannedInfo | null
  stationId: string | null
  scannedStationId: string | null
  shippingPath: {
    routeId: string
    sourceStation: {
      id: string
      sequence: number
    } | null
    targetStation: {
      id: string
      sequence: number
    } | null
    finished: boolean
  }[]
  sourceStationId: string | null
  targetStationId: string | null
  sender: EndUserInfo
  receiver: EndUserInfo
  deliveryInfo: {
    deliverTime: string
    imageUrl: string
    imageUrls: string[] | null
    handOverMethod: string
    specialNote: string
    location?: LocationContext
    type?:
      | "FORCE_DELIVERY"
      | "FIX_WRONG_DELIVERY"
      | "BEFORE_DELIVERY"
      | "FIX_BEFORE_DELIVERY"
    extraPayload?: any
  } | null
  holdDeliveryInfo: {
    holdTime?: string
    imageUrl: string
    imageUrls: string[] | null
    type: HoldDeliveryType
    reason: string
    specialNote: string
    location: LocationContext
    extraPayload?: {
      holdOrQuitTypeId?: string
      memo?: string
    }
  } | null
  holdPickUpInfo: {
    holdTime?: string
    imageUrl: string
    imageUrls: string[] | null
    type: HoldPickUpType
    reason: string
    specialNote: string
    location: LocationContext
    extraPayload?: {
      holdOrQuitTypeId?: string
      memo?: string
    }
  } | null
  unholdDeliveryInfo: {
    unholdTime: string
    unholdCount: number
    extraPayload: any
  } | null
  unholdPickUpInfo: {
    unholdTime: string
    unholdCount: number
    extraPayload: any
  } | null
  pickUpInfo: {
    pickUpTime?: string
    imageUrl?: string
    imageUrls?: string[]
    handOverMethod: string
    specialNote: string
    extraPayload: any
    location: LocationContext
  } | null
  quitInfo: {
    quitTime: string
    reason: QuitReasonType
    specialNote: string
    extraPayload: any
  } | null
  lastPossessor: {
    organizationId: Organization["id"] | ""
    driverId: string
    driverName: string
    driverPhone: string
  }
  forwardingInfo?: {
    brokerOrganizationName: string
    deliveryOrganizationId: string
    deliveryOrganizationName: string
    handoverTime: string
    invoiceNumber: string
  }
  developerPayload?: string
  reportTimes: {
    [key in ReportTimeKeys]?: string
  }
  scannedStationIds: string[]
  // XXX: 알림톡 대표 화물 정보인 경우 아래 필드들이 추가됨
  grouped?: boolean
  reserved?: boolean
  // packageItem의 경우 존재. 패키징된 패키징 송장의 ID
  packageLoadId?: string
  // 반송예약된 경우 해당 필드가 생성되며 값은 true
  returnReserved?: boolean
  // 물품부가정보 : FRESH(신선식품) / EXPENSIVE(고가물품)
  cautionTypes?: string[]
  // 체적 정보. 미측정 시 3변과 weight가 모두 0으로 내려옴
  dimension: { width: number; depth: number; height: number } // 단위 mm
  weight: number // 단위 g
}

interface EndUserInfo {
  shippingPlaceId?: string
  name: string
  phone: string
  rawAddress: string
  address: Address
  location: Location
  lastMileInfo: {
    regionId: string
    regionSetPolicyType: PolicyType
    regionSetName: string
  }
  accessMethod?: string
  preference?: string
}

export type LoadFilterType = string | ((load: Load) => boolean)

export type HoldDeliveryType =
  | "REFUSED"
  | "DATE_CHANGED"
  | "ADDRESS_CHANGED"
  | "WRONG_ADDRESS"
  | "DAMAGED"
  | "MISCLASSIFIED"
  | "DELAYED"
  | "WRONG_PASSWORD"
  | "CANNOT_FIND_ENTRANCE"
  | "ACCESS_DENIED"
  | "LOSS_CONCERNED"
  | "BAD_ENVIRONMENT"
  | "CANNOT_DO_REQUEST"
  | "LOST"

export type HoldPickUpType =
  | "CANCELED"
  | "DATE_CHANGED"
  | "ADDRESS_CHANGED"
  | "NO_LOAD"
  | "OTHER_COMPANY_LOAD"
  | "BAD_PACKING"
  | "CANNOT_DISTINGUISH_LOAD"
  | "WRONG_PASSWORD"
  | "ACCESS_DENIED"
  | "BAD_ENVIRONMENT"
  | "CANNOT_DO_REQUEST"

export type QuitReasonType = "DAMAGED" | "LOST" | "CLIENT_REQUEST" | "TEST"

export type LoadEventType =
  | "load_create"
  | "load_pick_up"
  | "load_deliver"
  | "load_quit"
  | "load_hold_collect"
  | "load_hold_delivery"

export function formatQuitReason(type: QuitReasonType) {
  switch (type) {
    case "DAMAGED":
      return "물품 파손"
    case "LOST":
      return "물품 분실"
    case "CLIENT_REQUEST":
      return "고객사 요청"
    case "TEST":
      return "테스트"
    default:
      return type
  }
}

export function getTime(
  load: Load | LoadWithOrderState,
  eventType: Extract<
    ReportEventTypes,
    "pickUp" | "holdPickUp" | "holdDelivery" | "deliver" | "quit"
  >,
  timeType: "real-time" | "report-time"
): dayjs.Dayjs | undefined {
  const convert = (time?: string) => (time ? dayjs(time) : undefined)
  const time =
    timeType === "report-time"
      ? load.reportTimes[`${eventType}Time`]
      : undefined
  if (time) {
    return convert(time)
  }
  switch (eventType) {
    case "pickUp":
      return convert(load.pickUpInfo?.pickUpTime)
    case "holdPickUp":
      return convert(load.holdPickUpInfo?.holdTime)
    case "holdDelivery":
      return convert(load.holdDeliveryInfo?.holdTime)
    case "deliver":
      return convert(load.deliveryInfo?.deliverTime)
    case "quit":
      return convert(load.quitInfo?.quitTime)
  }
}
